import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Button, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCancelFormDeliverable } from '../../components/form-list/useCancelFormDeliverable';
import { useDeleteApiTripReportsByIdMutation } from '../../app/athenaApiGen';
import { FormList } from '../../components/form-list';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import { WORKFLOW_PROGRESS_FILTERS } from '../../utils/workflow';
import { createTRColumns } from './TRUtils';
import { useTRList } from './useTRList';
import {
  openDeleteDialog,
  closeDeleteDialog,
  showSnackbar,
  hideSnackbar,
  setWorkflowFilter,
} from '../../app/tr/trSlice';
import { isFormTypeAvailable } from '../utilities';

export default function TRList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get state from Redux
  const { isDeleteDialogOpen, trToDelete, snackbar } = useSelector((state) => state.tr);
  const workflowFilter = useSelector((state) => state.tr.workflowFilter);
  const availableForms = useSelector((state) => state.user.availableForms);
  const isTarAvailable = isFormTypeAvailable(availableForms, 'tar');

  const {
    paginationModel,
    setPaginationModel,
    search,
    setSearch,
    rows,
    rowCount,
    loading,
    isError,
    refetch,
    quickFilterCounts,
  } = useTRList();
  const [deleteTripReport, { isLoading: deleting }] = useDeleteApiTripReportsByIdMutation();

  const columns = useMemo(() => createTRColumns(), []);

  const handleDeleteClick = useCallback((row) => {
    dispatch(openDeleteDialog({ id: row.id, trNumber: row.trNumber }));
  }, [dispatch]);

  const handleDeleteConfirm = useCallback(
    async () => {
      if (!trToDelete) return;
      try {
        await deleteTripReport({ id: trToDelete.id }).unwrap();
        dispatch(closeDeleteDialog());
        dispatch(showSnackbar({ message: 'Trip Report deleted successfully', severity: 'success' }));
        refetch();
      } catch (error) {
        console.error('Failed to delete Trip Report', error);
        dispatch(closeDeleteDialog());
        dispatch(showSnackbar({ message: 'Failed to delete Trip Report. Please try again.', severity: 'error' }));
      }
    },
    [trToDelete, deleteTripReport, refetch, dispatch],
  );

  const notifyCancel = useCallback(
    (message, severity) => dispatch(showSnackbar({ message, severity })),
    [dispatch],
  );
  const { handleCancelClick } = useCancelFormDeliverable({
    entityLabel: 'Trip Report',
    notify: notifyCancel,
    refetch,
  });

  const rowActions = useMemo(
    () => [
      {
        label: 'View',
        onClick: (row) => navigate(`/tr/${row.id}?mode=view`),
      },
      {
        label: 'Edit',
        onClick: (row) => navigate(`/tr/${row.id}?mode=edit`),
        hidden: (row) => row.workflowState && row.workflowState !== 'Pending',
      },
      {
        label: 'Cancel',
        onClick: (row) => handleCancelClick(row),
        hidden: (row) =>
          !row.workflowState ||
          row.workflowState === 'Pending' ||
          row.workflowState !== 'In Internal Review',
      },
      {
        label: 'Delete',
        onClick: (row) => handleDeleteClick(row),
        hidden: (row) => row.workflowState && row.workflowState !== 'Pending',
      },
      {
        label: 'View TAR',
        onClick: (row) => navigate(`/tar/${row.tarId}`),
        hidden: (row) => !row.tarId,
      },
    ],
    [handleDeleteClick, handleCancelClick, navigate],
  );

  return (
    <Box sx={{ p: 1, pt: 2, height: '100%', bgcolor: 'transparent' }}>
      <FormList
        title="Trip Reports"
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        loading={loading}
        error={isError}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowActions={rowActions}
        onRowClick={(row) => navigate(`/tr/${row.id}?mode=view`)}
        workflowFilters={WORKFLOW_PROGRESS_FILTERS}
        workflowFilter={workflowFilter}
        onWorkflowFilterChange={(value) => dispatch(setWorkflowFilter(value))}
        quickFilterCounts={quickFilterCounts}
        search={search}
        onSearchChange={setSearch}
        onRefresh={refetch}
        createButtonLabel="New TR"
        onCreateClick={() => navigate('/tr')}
        additionalHeaderButtons={
          isTarAvailable ? (
            <Button
              size="small"
              variant="outlined"
              onClick={() => navigate('/tar-list')}
              sx={{
                textTransform: 'none',
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
                '&:active': {
                  boxShadow: 'none',
                },
                '&:focus': {
                  boxShadow: 'none',
                },
              }}
            >
              View TARs
            </Button>
          ) : null
        }
      />

      <DeleteConfirmation
        open={isDeleteDialogOpen}
        title="Delete Trip Report"
        onClose={() => dispatch(closeDeleteDialog())}
        content={{
          mainText: `
            Are you sure you want to delete Trip Report <strong>${trToDelete?.trNumber}</strong>?
            <br /><br />
            This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => dispatch(hideSnackbar())}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => dispatch(hideSnackbar())}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
